import { Checkbox, Radio, Select, Space } from "antd";
import { Form, Formik } from "formik";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { TRESO_ELEMENTS } from "../../../../data/constants";
import {
  exportSynthesisTreasury,
  resetExportStatus,
} from "../../../../data/slices/exports";
import { closeModal } from "../../../../data/slices/modals";
import { processAccountTresoFromObject } from "../../../../hooks/useFiltersAndDate";
import { parseDate } from "../../../../utilities/filters";
import { getAccountBadgeStyles } from "../../../../utilities/colorUtils";
import Button from "../../../Button";
import Overlay from "../../../Overlay";
import HeaderOverlay from "../../../Project/Header";
import Title from "../../../Project/Title";
import CustomRangePicker from "../../../RangePicker";
import "./index.scss";

const { Option } = Select;

function ExportSynthesisTreso({ open, onCancel, onContinue, data, ...rest }) {
  const dispatch = useDispatch();
  // Form validation schema
  const ExportSchema = Yup.object().shape({
    dateRange: Yup.array()
      .of(Yup.mixed())
      .length(2, "Veuillez sélectionner une période")
      .required("Veuillez sélectionner une période"),
    levelType: Yup.string().required("Veuillez sélectionner un type de niveau"),
    categoryLevels: Yup.number().test(
      "categoryLevels-custom",
      "Veuillez sélectionner le nombre de niveaux",
      function (value) {
        // If levelType is "custom", categoryLevels must be required and between 0 and 3
        if (this.parent.levelType === "custom") {
          if (typeof value !== "number" || isNaN(value)) return false;
          if (value < 1)
            return this.createError({ message: "Minimum 1 niveau" });
          if (value > 5)
            return this.createError({ message: "Maximum 5 niveaux" });
          return true;
        }
        // If not custom, categoryLevels is not required
        return true;
      }
    ),
    filterType: Yup.string().required(
      "Veuillez sélectionner un type de filtre"
    ),
    selectedFilters: Yup.array().when("filterType", {
      is: "apply",
      then: (schema) =>
        schema.min(1, "Veuillez sélectionner au moins un filtre"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const { accounts } = useSelector((state) => state.accounts);
  const { exportStatus, exportError, lastExportResult } = useSelector(
    (state) => state.exports
  );
  const { selectedMonths } = useSelector((state) => state.layout);

  const getStartAndEndDateSideBar = () => {
    const start = selectedMonths?.map((month) => parseDate(month))[0];
    const end = selectedMonths?.map((month) => parseDate(month))[
      selectedMonths?.length - 1
    ];

    return { start, end };
  };

  const { start, end } = getStartAndEndDateSideBar();

  // Initial form values
  const initialValues = useMemo(
    () => ({
      dateRange: [start, end], // Date range for the picker
      levelType: "all", // "all" for "Tous les niveaux", "custom" for dropdown option
      categoryLevels: 1,
      filterType: "none", // "none" for "Aucun filtre", "apply" for "Appliquer un filtre"
      selectedAccounts: {}, // Treasury account filters
      noFilterCheckedLocal: false,
      allFiltersChecked: false,
      allPointedChecked: false,
      excludeMaskedEmptyCategories: true,
    }),
    [start, end]
  );

  // Helper functions for treasury filter handling (similar to Filter popover)
  const checkIfAllFiltersAreSelected = (selectedAccounts) => {
    if (!accounts?.length) return false;

    return accounts.every((account) => {
      const accountStatuses = selectedAccounts[account.id] || [];
      const requiredStatuses = [
        ...TRESO_ELEMENTS.map((child) => child.status),
        "réel",
      ];
      return requiredStatuses.every((status) =>
        accountStatuses.includes(status)
      );
    });
  };

  const checkIfAllPointedAreSelected = (selectedAccounts) => {
    if (!accounts?.length) return false;

    return accounts.every((account) => {
      const accountStatuses = selectedAccounts[account.id] || [];
      return (
        accountStatuses.length === 2 &&
        accountStatuses.includes("pointé") &&
        accountStatuses.includes("réel")
      );
    });
  };

  // Reset export status when modal opens
  useEffect(() => {
    if (open) {
      dispatch(resetExportStatus());
    }
  }, [open, dispatch]);

  // Handle export success/error
  useEffect(() => {
    if (exportStatus === "succeeded" && lastExportResult) {
      // File download is handled automatically in the slice
      console.log("Export completed successfully");
    }
  }, [exportStatus, lastExportResult]);

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    try {
      // Format dates for API
      const startDate = values.dateRange?.[0]?.format("YYYY-MM");
      const endDate = values.dateRange?.[1]?.format("YYYY-MM");

      const at = new Date().toISOString();

      let maxCategoryDepth;
      if (values.levelType === "all") {
        maxCategoryDepth = 5;
      } else {
        maxCategoryDepth = values.categoryLevels;
      }

      // Transform treasury accounts to filter options format
      const filterOptions =
        values.filterType === "apply"
          ? processAccountTresoFromObject(values.selectedAccounts)
          : [];

      const exportData = {
        startDate,
        endDate,
        maxCategoryDepth,
        at,
        filterOptions,
        excludeMaskedEmptyCategories: values.excludeMaskedEmptyCategories,
      };

      await dispatch(
        exportSynthesisTreasury({
          projectId: data?.projectId,
          tableType: "categories",
          mode: "treso",
          exportData,
        })
      ).then((res) => {
        if (res.meta.requestStatus === "rejected") {
          return;
        } else {
          // Reset form values to initial state after successful export
          resetForm({
            values: {
              dateRange: [start, end],
              levelType: "all",
              categoryLevels: 5,
              filterType: "none",
              selectedAccounts: {},
              noFilterCheckedLocal: false,
              allFiltersChecked: false,
              allPointedChecked: false,
              excludeMaskedEmptyCategories: true,
            },
          });
          dispatch(closeModal("export-synthesis-treso-modal"));
        }
      });
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm) => {
    // Reset form values when canceling
    if (resetForm) {
      resetForm({
        values: {
          dateRange: [start, end],
          levelType: "all",
          categoryLevels: 5,
          filterType: "none",
          selectedAccounts: {},
          noFilterCheckedLocal: false,
          allFiltersChecked: false,
          allPointedChecked: false,
          excludeMaskedEmptyCategories: true,
        },
      });
    }

    if (typeof onCancel === "function") {
      onCancel();
    }
  };

  const handleCheckAllFilters = (checked, setFieldValue, values) => {
    if (checked) {
      const updatedAccounts = {};
      accounts?.forEach((account) => {
        updatedAccounts[account.id] = [
          ...TRESO_ELEMENTS.map((child) => child.status),
          "réel",
        ];
      });
      setFieldValue("selectedAccounts", updatedAccounts);
      setFieldValue("noFilterCheckedLocal", false);
    } else {
      setFieldValue("selectedAccounts", {});
    }
    setFieldValue("allFiltersChecked", checked);
    setFieldValue("allPointedChecked", false);
  };

  const handleCheckAllPointed = (checked, setFieldValue) => {
    if (checked) {
      const updatedAccounts = {};
      accounts?.forEach((account) => {
        updatedAccounts[account.id] = ["pointé", "réel"];
      });
      setFieldValue("selectedAccounts", updatedAccounts);
      setFieldValue("noFilterCheckedLocal", false);
      setFieldValue("allFiltersChecked", false);
    } else {
      setFieldValue("selectedAccounts", {});
    }
    setFieldValue("allPointedChecked", checked);
  };

  const handleParentChange = (
    accountTresoId,
    checked,
    setFieldValue,
    values
  ) => {
    const updatedAccounts = { ...values.selectedAccounts };

    if (checked) {
      updatedAccounts[accountTresoId] = TRESO_ELEMENTS.map(
        (child) => child.status
      );
      updatedAccounts[accountTresoId].push("réel");
    } else {
      delete updatedAccounts[accountTresoId];
    }

    setFieldValue("selectedAccounts", updatedAccounts);
    setFieldValue("noFilterCheckedLocal", false);
    setFieldValue(
      "allFiltersChecked",
      checkIfAllFiltersAreSelected(updatedAccounts)
    );
    setFieldValue(
      "allPointedChecked",
      checkIfAllPointedAreSelected(updatedAccounts)
    );
  };

  const handleChildChange = (
    accountTresoId,
    child,
    checked,
    setFieldValue,
    values
  ) => {
    const updatedAccounts = { ...values.selectedAccounts };

    if (checked) {
      updatedAccounts[accountTresoId] = updatedAccounts[accountTresoId]
        ? [...updatedAccounts[accountTresoId], child.status]
        : [child.status];
    } else {
      const filteredStatuses = updatedAccounts[accountTresoId]?.filter(
        (status) => status !== child.status
      );
      if (filteredStatuses?.length) {
        updatedAccounts[accountTresoId] = filteredStatuses;
      } else {
        delete updatedAccounts[accountTresoId];
      }
    }

    // Auto-add "réel" type when relevant statuses are selected
    const statusesToCheck = ["prévisionnel", "engagé", "pointé"];
    Object.keys(updatedAccounts).forEach((id) => {
      const accountStatuses = updatedAccounts[id];
      const hasRelevantStatus = accountStatuses.some((status) =>
        statusesToCheck.includes(status)
      );

      if (hasRelevantStatus && !accountStatuses.includes("réel")) {
        accountStatuses.push("réel");
      } else if (!hasRelevantStatus) {
        const filteredStatuses = accountStatuses.filter(
          (status) => status !== "réel"
        );
        if (filteredStatuses.length) {
          updatedAccounts[id] = filteredStatuses;
        } else {
          delete updatedAccounts[id];
        }
      }
    });

    setFieldValue("selectedAccounts", updatedAccounts);
    setFieldValue("noFilterCheckedLocal", false);
    setFieldValue(
      "allFiltersChecked",
      checkIfAllFiltersAreSelected(updatedAccounts)
    );
    setFieldValue(
      "allPointedChecked",
      checkIfAllPointedAreSelected(updatedAccounts)
    );
  };

  const isChildChecked = (accountTresoId, childStatus, values) => {
    const accountStatuses = values.selectedAccounts[accountTresoId] || [];
    return accountStatuses.includes(childStatus);
  };

  const isParentSelected = (accountTresoId, values) => {
    return !!values.selectedAccounts[accountTresoId];
  };

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo className={"export-synthesis-treasury-overlay"}>
        <Title
          className={"export-synthesis-treasury-title"}
          isDelete
          subTitle={`Export d'une synthèse de trésorerie`}
        />

        <Formik
          initialValues={initialValues}
          validationSchema={ExportSchema}
          onSubmit={handleFormSubmit}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => {
            return (
              <Form className="export-synthesis-form" onSubmit={handleSubmit}>
                <div className="export-synthesis-content">
                  <div className="export-synthesis-content-wrapper">
                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">1)</div>
                        <div className="section-title">
                          Choisissez la période à exporter:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="date-range-container">
                          <CustomRangePicker
                            selectedMonthRange={values.dateRange}
                            handleMonthChange={(dateRange, saveDateChecked) => {
                              setFieldValue("dateRange", dateRange);
                            }}
                            className={"custom-range-picker-synthese"}
                            isSyntheseSelected={false}
                            label="Période"
                            index={0}
                            hasDivider={true}
                          />
                          {errors.dateRange && touched.dateRange && (
                            <div className="error-message">
                              {errors.dateRange}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">2)</div>
                        <div className="section-title">
                          Choisissez jusqu'à combien de niveaux de
                          sous-catégories vous souhaitez exporter votre
                          synthèse:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <Radio.Group
                            onChange={(e) => {
                              setFieldValue("levelType", e.target.value);
                              if (e.target.value !== "custom") {
                                setFieldValue("categoryLevels", 1);
                              }
                            }}
                            value={values.levelType}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            <Radio value="all">Tous les niveaux</Radio>

                            <Space>
                              <Radio value="custom" />
                              <Select
                                value={String(values.categoryLevels)}
                                disabled={values.levelType !== "custom"}
                                onChange={(val) =>
                                  setFieldValue("categoryLevels", Number(val))
                                }
                                style={{ minWidth: 300 }}
                              >
                                <Option value="1">
                                  Uniquement les catégories principales
                                </Option>
                                <Option value="2">
                                  avec 1 niveau de sous-catégories
                                </Option>
                                <Option value="3">
                                  avec 2 niveaux de sous-catégories
                                </Option>
                                <Option value="4">
                                  avec 3 niveaux de sous-catégories
                                </Option>
                              </Select>
                            </Space>
                          </Radio.Group>

                          {errors.levelType && touched.levelType && (
                            <div className="error-message">
                              {errors.levelType}
                            </div>
                          )}
                          {errors.categoryLevels && touched.categoryLevels && (
                            <div className="error-message">
                              {errors.categoryLevels}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">3)</div>
                        <div className="section-title">
                          Choisissez un filtre au niveau des écritures:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <Radio.Group
                            onChange={(e) => {
                              setFieldValue("filterType", e.target.value);
                              // Reset selected filters if switching to "none"
                              if (e.target.value !== "apply") {
                                setFieldValue("selectedFilters", []);
                              }
                            }}
                            value={values.filterType}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            <div className="filter-option-item">
                              <Radio value="none">Aucun filtre</Radio>
                            </div>

                            <div className="filter-option-item">
                              <Radio value="apply">Appliquer un filtre</Radio>

                              {values.filterType === "apply" && (
                                <div className="filter-checkboxes-container">
                                  <div className="treasury-filter-header">
                                    <Checkbox
                                      className="checkbox-all-filters"
                                      checked={values.allFiltersChecked}
                                      onChange={(e) =>
                                        handleCheckAllFilters(
                                          e.target.checked,
                                          setFieldValue,
                                          values
                                        )
                                      }
                                    >
                                      Tout sélectionner
                                    </Checkbox>

                                    <Checkbox
                                      className="checkbox-all-filters"
                                      checked={values.allPointedChecked}
                                      onChange={(e) =>
                                        handleCheckAllPointed(
                                          e.target.checked,
                                          setFieldValue
                                        )
                                      }
                                    >
                                      Statut pointé (tous les comptes)
                                    </Checkbox>
                                  </div>

                                  {accounts?.map((account) => (
                                    <div
                                      key={account.id}
                                      className="options-group"
                                    >
                                      <Checkbox
                                        checked={isParentSelected(
                                          account.id,
                                          values
                                        )}
                                        onChange={(e) =>
                                          handleParentChange(
                                            account.id,
                                            e.target.checked,
                                            setFieldValue,
                                            values
                                          )
                                        }
                                      >
                                        {account?.name}
                                      </Checkbox>

                                      <div className="filter-children">
                                        {TRESO_ELEMENTS?.map((child) => (
                                          <Checkbox
                                            key={child.id}
                                            checked={isChildChecked(
                                              account.id,
                                              child.status,
                                              values
                                            )}
                                            onChange={(e) =>
                                              handleChildChange(
                                                account.id,
                                                child,
                                                e.target.checked,
                                                setFieldValue,
                                                values
                                              )
                                            }
                                          >
                                            <div
                                              className="elements-types"
                                              style={{
                                                background:
                                                  child?.backgroundColor,
                                              }}
                                            >
                                              <div>{child.label}</div>
                                              <div
                                                className="compte-displayer-filter"
                                                style={getAccountBadgeStyles(
                                                  account?.color
                                                )}
                                              >
                                                {account?.code}
                                              </div>
                                            </div>
                                          </Checkbox>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </Radio.Group>

                          {errors.filterType && touched.filterType && (
                            <div className="error-message">
                              {errors.filterType}
                            </div>
                          )}
                          {errors.selectedFilters &&
                            touched.selectedFilters && (
                              <div className="error-message">
                                {errors.selectedFilters}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">4)</div>
                        <div className="section-title">Ne pas exporter :</div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <div className="filter-option-item">
                            <Radio
                              checked={!!values.excludeMaskedEmptyCategories}
                              onClick={(e) => {
                                e.preventDefault();
                                setFieldValue(
                                  "excludeMaskedEmptyCategories",
                                  !values.excludeMaskedEmptyCategories
                                );
                              }}
                            >
                              Les catégories masquées (si elles sont vides !)
                            </Radio>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="verify-inputs-popup">
                    {exportError && (
                      <div className="error-message export-error">
                        {exportError}
                      </div>
                    )}
                    <div className="action-buttons">
                      <Button
                        text={"Annuler"}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCancel(resetForm);
                        }}
                        className={"action-button"}
                        htmlType="button"
                        disabled={isSubmitting || exportStatus === "loading"}
                      />
                      <Button
                        text={"Continuer"}
                        htmlType="submit"
                        className={"action-button agree-action-button"}
                        disabled={isSubmitting || exportStatus === "loading"}
                        loading={isSubmitting || exportStatus === "loading"}
                      />
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </HeaderOverlay>
    </Overlay>
  );
}

export default ExportSynthesisTreso;
